/*
 * LUXE Live Chat — widget runtime.
 * Section markup lives in sections/luxe-live-chat.liquid.
 * Backend (Worker + Discord relay) lives in projects/live-chat/ in the AI-MINION repo.
 */

(function () {
  "use strict";

  // Version marker — if you DON'T see this in DevTools console, the browser is serving a stale cached copy.
  console.log("[luxe-live-chat] build 2026-05-16-b");

  if (window.__luxeLiveChatInitialised) return;
  window.__luxeLiveChatInitialised = true;

  function initRoot(root) {
    if (root.dataset.luxeReady === "1") return;
    root.dataset.luxeReady = "1";

    var workerUrl = (root.dataset.workerUrl || "").replace(/\/$/, "");
    if (!workerUrl) {
      console.warn("[luxe-live-chat] no worker URL configured; widget disabled.");
      return;
    }

    var launcher = root.querySelector("[data-luxe-live-chat-launcher]");
    var panel = root.querySelector("[data-luxe-live-chat-panel]");
    var closeBtn = root.querySelector("[data-luxe-live-chat-close]");
    var transcript = root.querySelector("[data-luxe-live-chat-transcript]");
    var composer = root.querySelector("[data-luxe-live-chat-composer]");
    var input = root.querySelector("[data-luxe-live-chat-input]");
    var sendBtn = root.querySelector("[data-luxe-live-chat-send]");
    var nameInput = root.querySelector("[data-luxe-live-chat-name]");
    var emailInput = root.querySelector("[data-luxe-live-chat-email]");

    if (!launcher || !panel || !transcript || !composer || !input) return;

    var introMessageLive = root.dataset.introMessage || "";
    var introMessageOfflineHours = root.dataset.introMessageOfflineHours || introMessageLive;
    // Which intro to show on panel open. Set by /status response. Defaults to "live" if status fails.
    var currentIntroMessage = introMessageLive;
    var teamLabel = root.dataset.teamLabel || "Luxecare team";
    var youLabel = root.dataset.youLabel || "You";
    var thinkingText = root.dataset.thinkingText || "";

    var pageContext = buildPageContext(root);
    var ws = null;
    var initSent = false;
    var sessionId = null;
    var introShown = false;
    var pendingNode = null;
    var seenTimestamps = new Set();
    var unreadCount = 0;
    var unreadBadge = launcher.querySelector("[data-luxe-live-chat-unread]");
    var reconnectTimer = null;
    var reconnectDelay = 1000; // exponential backoff base
    var cartFetched = false;
    var unloadFired = false; // de-dupe pagehide + beforeunload firing in sequence

    // Restore a prior session ID from this tab's sessionStorage (survives page reload).
    // localStorage would survive new tabs too, which is usually unwanted for a chat session.
    var SESSION_STORAGE_KEY = "luxe-live-chat-sid";
    try {
      var savedSid = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (savedSid) sessionId = savedSid;
    } catch (e) { /* sessionStorage blocked (e.g. iframe sandbox) — fine */ }

    // Reveal the wrapper (was [hidden] on first render)
    root.removeAttribute("hidden");

    // Initial status check.
    // `online: false` → widget fully hidden (manual /chat-offline by team)
    // `mode: "offline-hours"` → widget shown but uses the offline-hours intro
    // `mode: "live"` (default) → widget shown with the standard intro
    fetchStatus().then(function (status) {
      if (status && status.online === false) {
        root.style.display = "none";
        return;
      }
      if (status && status.mode === "offline-hours" && introMessageOfflineHours) {
        currentIntroMessage = introMessageOfflineHours;
      }
    });

    // Capture-phase listener on document so we get the click BEFORE any potential
    // parent interceptor (theme add-to-cart form, modal overlays, etc.) can stop it.
    document.addEventListener(
      "click",
      function (ev) {
        var t = ev.target;
        if (!t || !t.closest) return;
        if (t.closest("[data-luxe-live-chat-launcher]") === launcher) {
          ev.preventDefault();
          ev.stopPropagation();
          openPanel();
        } else if (t.closest("[data-luxe-live-chat-close]") === closeBtn) {
          ev.preventDefault();
          ev.stopPropagation();
          closePanel();
        }
      },
      true,
    );

    composer.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var text = (input.value || "").trim();
      if (!text) return;
      input.value = "";
      sendMessage(text);
    });

    // Identity fields — debounced send of `identify` payloads on change. Longer debounce
    // (2s) so users who type name + email in one go produce a single Discord notification
    // rather than one per field. Slow typists with long pauses still produce multiple posts.
    var identifyTimer = null;
    function scheduleIdentify() {
      if (identifyTimer) clearTimeout(identifyTimer);
      identifyTimer = setTimeout(function () {
        identifyTimer = null;
        sendIdentify();
      }, 2000);
    }
    function sendIdentify() {
      if (!ws || ws.readyState !== 1) return; // only send when connected
      var name = nameInput ? (nameInput.value || "").trim() : "";
      var email = emailInput ? (emailInput.value || "").trim() : "";
      ws.send(JSON.stringify({ type: "identify", name: name, email: email }));
    }
    if (nameInput) nameInput.addEventListener("input", scheduleIdentify);
    if (emailInput) emailInput.addEventListener("input", scheduleIdentify);

    // Close on Escape when panel is open
    document.addEventListener("keydown", function (ev) {
      if (ev.key === "Escape" && panel.getAttribute("data-luxe-open") === "true") {
        closePanel();
      }
    });

    // Close WS cleanly when the visitor leaves the page (different code so the DO can distinguish).
    // `beforeunload` doesn't fire reliably on mobile (iOS Safari, swipe-close, OS-killed tabs).
    // `pagehide` is the recommended modern equivalent — fires in ~95% of unload scenarios.
    function notifyUnload() {
      if (unloadFired) return;
      unloadFired = true;
      if (ws && ws.readyState <= 1) {
        try { ws.close(4001, "page unload"); } catch (e) { /* ignore */ }
      }
    }
    window.addEventListener("beforeunload", notifyUnload);
    window.addEventListener("pagehide", notifyUnload);

    function fetchStatus() {
      return fetch(workerUrl + "/status", { credentials: "omit" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }

    function ensureCart() {
      if (cartFetched) return;
      cartFetched = true;
      fetch("/cart.js", { credentials: "same-origin" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          pageContext.cart = {
            itemCount: cart.item_count,
            currency: cart.currency || "£",
            total: (cart.total_price || 0) / 100,
            items: (cart.items || []).slice(0, 10).map(function (i) {
              return {
                title: i.product_title,
                quantity: i.quantity,
                variant: i.variant_title || undefined,
              };
            }),
          };
        })
        .catch(function () { /* cart is optional context */ });
    }

    function openPanel() {
      lockBodyScroll(); // mobile only — prevents background page zoom/pan when input is focused
      showPanel();
      ensureCart(); // lazy-fetch the cart only when visitor actually opens the chat
      if (!introShown) {
        introShown = true;
        if (currentIntroMessage) appendSystem(currentIntroMessage);
      }
      input.focus();

      // Clear any unread notification — visitor is now looking at the panel
      clearUnread();

      // Tell the server we're back open (only matters if a session is already running)
      if (ws && ws.readyState === 1) {
        try { ws.send(JSON.stringify({ type: "panel-state", state: "open" })); } catch (e) { /* ignore */ }
      } else if (sessionId && (!ws || ws.readyState > 1)) {
        // Fallback: if the WS died (network drop, etc.), reconnect so future team replies still land
        ensureSocket().catch(function () { /* silent */ });
      }
    }

    function showPanel() {
      panel.hidden = false;
      // Allow next paint to apply the transition + scroll to bottom (any messages that
      // arrived while panel was hidden get scrolled into view straight away)
      requestAnimationFrame(function () {
        panel.setAttribute("data-luxe-open", "true");
        try {
          transcript.scrollTo({ top: transcript.scrollHeight, behavior: "instant" });
        } catch (e) {
          transcript.scrollTop = transcript.scrollHeight;
        }
      });
      launcher.setAttribute("aria-expanded", "true");
    }

    function closePanel() {
      panel.removeAttribute("data-luxe-open");
      launcher.setAttribute("aria-expanded", "false");
      unlockBodyScroll();
      setTimeout(function () {
        panel.hidden = true;
      }, 220);
      // Don't close the WS — keep it open so team replies can arrive and trigger the
      // unread-notification badge. Just tell the server we minimised.
      if (ws && ws.readyState === 1) {
        try { ws.send(JSON.stringify({ type: "panel-state", state: "minimised" })); } catch (e) { /* ignore */ }
      }
    }

    function sendMessage(text) {
      ensureSocket()
        .then(function () {
          if (!initSent) {
            initSent = true;
            // Refresh identity from form fields right before sending — they may have been
            // filled in between page load and the first send.
            if (nameInput) pageContext.visitorName = nameInput.value.trim() || undefined;
            if (emailInput) pageContext.visitorEmail = emailInput.value.trim() || undefined;
            ws.send(JSON.stringify({ type: "init", context: pageContext }));
          }
          ws.send(JSON.stringify({ type: "message", text: text }));
          appendMessage({ author: "visitor", text: text, ts: Date.now() });
          showPending();
        })
        .catch(function () {
          appendSystem("Sorry — we couldn't connect. Please try again in a moment.");
        });
    }

    function ensureSocket() {
      return new Promise(function (resolve, reject) {
        if (ws && ws.readyState === 1) return resolve();
        if (ws && ws.readyState === 0) {
          // Connecting — wait
          ws.addEventListener("open", function once() { resolve(); });
          ws.addEventListener("error", function () { reject(); });
          return;
        }

        var url = workerUrl.replace(/^http/, "ws") + "/session";
        if (sessionId) url += "?sid=" + encodeURIComponent(sessionId);

        ws = new WebSocket(url);
        ws.addEventListener("open", function () {
          reconnectDelay = 1000; // reset backoff on successful connect
          resolve();
        });
        ws.addEventListener("error", function () { reject(); });
        ws.addEventListener("message", onWsMessage);
        ws.addEventListener("close", function (ev) {
          initSent = false;
          // Auto-reconnect on unexpected close (network drop, server restart, etc.)
          // Skip if visitor explicitly left the page (4001) or if no prior session exists.
          if (ev.code !== 4001 && sessionId && !unloadFired && !reconnectTimer) {
            reconnectTimer = setTimeout(function () {
              reconnectTimer = null;
              ensureSocket().catch(function () {
                // exponential backoff for next attempt
                reconnectDelay = Math.min(reconnectDelay * 2, 30000);
              });
            }, reconnectDelay);
          }
        });
      });
    }

    function onWsMessage(ev) {
      var msg;
      try { msg = JSON.parse(ev.data); } catch (e) { return; }

      if (msg.type === "hello") {
        sessionId = msg.sessionId;
        try { sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId); } catch (e) { /* ignore */ }

        // Replay history. If the DOM is empty (fresh page load after a reload), replay
        // everything including visitor messages so the visitor has their conversation back.
        // If the DOM already has content (minimise/reopen on same page), skip visitor
        // messages — they're already in the DOM and the seenTimestamps dedupe wouldn't
        // catch them (server uses a different `ts` than the client-side render).
        if (Array.isArray(msg.history)) {
          var freshPage = seenTimestamps.size === 0;
          for (var i = 0; i < msg.history.length; i++) {
            var entry = msg.history[i];
            if (entry.author === "team") {
              appendMessage(entry);
            } else if (entry.author === "system") {
              appendSystem(entry.text, entry.ts);
            } else if (freshPage && entry.author === "visitor") {
              appendMessage(entry);
            }
          }
        }
        return;
      }
      if (msg.type === "ack") {
        return;
      }
      if (msg.type === "message" && msg.entry) {
        hidePending();
        if (msg.entry.author === "team") {
          appendMessage(msg.entry);
          // If panel is minimised, bump the unread badge so the visitor knows there's a reply
          if (isPanelMinimised()) bumpUnread();
        } else if (msg.entry.author === "system") {
          appendSystem(msg.entry.text);
        }
      }
    }

    function showPending() {
      if (!thinkingText) return;
      if (!pendingNode) {
        pendingNode = document.createElement("div");
        pendingNode.className = "luxe-live-chat__pending";
        pendingNode.textContent = thinkingText;
      }
      // appendChild moves the node to the end if it's already in the DOM, so the pending
      // indicator always sits below the latest visitor message instead of getting stranded.
      transcript.appendChild(pendingNode);
      scrollToBottom();
    }

    function hidePending() {
      if (pendingNode && pendingNode.parentNode) {
        pendingNode.parentNode.removeChild(pendingNode);
      }
      pendingNode = null;
    }

    function isPanelMinimised() {
      return panel.hasAttribute("hidden") || panel.getAttribute("data-luxe-open") !== "true";
    }

    function bumpUnread() {
      unreadCount += 1;
      root.setAttribute("data-luxe-unread", String(unreadCount));
      if (unreadBadge) unreadBadge.textContent = unreadCount > 9 ? "9+" : String(unreadCount);
    }

    function clearUnread() {
      if (unreadCount === 0) return;
      unreadCount = 0;
      root.removeAttribute("data-luxe-unread");
      if (unreadBadge) unreadBadge.textContent = "";
    }

    function appendMessage(entry) {
      // Dedupe in case server replays
      var key = entry.author + ":" + entry.ts + ":" + entry.text;
      if (seenTimestamps.has(key)) return;
      seenTimestamps.add(key);

      var row = document.createElement("div");
      row.className = "luxe-live-chat__message luxe-live-chat__message--" + entry.author;

      var meta = document.createElement("div");
      meta.className = "luxe-live-chat__message-meta";

      var author = document.createElement("span");
      author.className = "luxe-live-chat__message-author";
      author.textContent = entry.author === "visitor" ? youLabel : entry.authorName || teamLabel;
      meta.appendChild(author);

      var time = document.createElement("span");
      time.className = "luxe-live-chat__message-time";
      time.textContent = new Date(entry.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      meta.appendChild(time);

      var bubble = document.createElement("div");
      bubble.className = "luxe-live-chat__message-bubble";
      bubble.textContent = entry.text;

      row.appendChild(meta);
      row.appendChild(bubble);
      transcript.appendChild(row);
      scrollToBottom();
    }

    function appendSystem(text, ts) {
      if (ts != null) {
        var key = "system:" + ts + ":" + text;
        if (seenTimestamps.has(key)) return;
        seenTimestamps.add(key);
      }
      var row = document.createElement("div");
      row.className = "luxe-live-chat__message luxe-live-chat__message--system";
      var bubble = document.createElement("div");
      bubble.className = "luxe-live-chat__message-bubble";
      bubble.textContent = text;
      row.appendChild(bubble);
      transcript.appendChild(row);
      scrollToBottom();
    }

    function scrollToBottom() {
      transcript.scrollTop = transcript.scrollHeight;
    }
  }

  function buildPageContext(root) {
    var nameInput = root.querySelector("[data-luxe-live-chat-name]");
    var emailInput = root.querySelector("[data-luxe-live-chat-email]");
    return {
      url: window.location.href,
      productHandle: root.dataset.productHandle || undefined,
      productTitle: root.dataset.productTitle || undefined,
      variantTitle: root.dataset.variantTitle || undefined,
      wrapMessage: root.dataset.wrapMessage || undefined,
      visitorName: nameInput && nameInput.value.trim() ? nameInput.value.trim() : undefined,
      visitorEmail: emailInput && emailInput.value.trim() ? emailInput.value.trim() : undefined,
    };
    // Cart is fetched lazily on first panel open — see ensureCart() inside initRoot.
  }

  // Mobile viewport handling — iOS Safari fixes
  //
  // Two intertwined iOS quirks:
  //  1. `position: fixed` is positioned relative to the LAYOUT viewport; when the on-screen
  //     keyboard appears the visual viewport shrinks but position:fixed elements don't
  //     reposition correctly → composer ends up behind the keyboard.
  //  2. Tapping an input that triggers focus can cause iOS to auto-zoom (if font-size <16px)
  //     AND make `position: fixed` elements render outside their visible bounds → "the page
  //     fucks up" symptom.
  //
  // Mitigations applied:
  //  - sync visualViewport.height into a CSS custom property the mobile panel reads
  //  - lock body scroll when panel open on mobile (prevents background zoom/pan interfering)
  //  - font-size: 16px !important on inputs (handled in CSS, prevents auto-zoom)
  // Keep --luxe-live-chat-vvh (height) and --luxe-live-chat-vvt (top offset) in sync with
  // the visual viewport so the mobile full-screen panel follows the visible area as iOS
  // scrolls the layout viewport (e.g. when an input is focused and iOS scrolls to make
  // the input visible above the keyboard).
  function syncVisualViewport() {
    if (!window.visualViewport) return;
    var vv = window.visualViewport;
    var root = document.documentElement;
    root.style.setProperty("--luxe-live-chat-vvh", vv.height + "px");
    root.style.setProperty("--luxe-live-chat-vvt", vv.offsetTop + "px");
  }
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", syncVisualViewport);
    window.visualViewport.addEventListener("scroll", syncVisualViewport);
    syncVisualViewport();
  }

  // Lock html+body overflow while the mobile panel is open. Plain overflow:hidden works
  // on iOS (no position:fixed dance needed) because the panel uses visualViewport-tracked
  // top + height — it follows whatever iOS does to the visible area.
  function lockBodyScroll() {
    if (!window.matchMedia("(max-width: 480px)").matches) return;
    document.documentElement.classList.add("luxe-live-chat-body-locked");
    document.body.classList.add("luxe-live-chat-body-locked");
  }
  function unlockBodyScroll() {
    document.documentElement.classList.remove("luxe-live-chat-body-locked");
    document.body.classList.remove("luxe-live-chat-body-locked");
  }

  // Bootstrap
  function init() {
    var roots = document.querySelectorAll("[data-luxe-live-chat]");
    Array.prototype.forEach.call(roots, initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
